export type MaybeReadonlyArray<T> = readonly T[] | T[] | null | undefined;

const isArrayValue = <T>(value: T | MaybeReadonlyArray<T>): value is readonly T[] | T[] => Array.isArray(value);

/**
 * Normalizes values that may already be an array or may be nullish.
 * Returns a shallow copy so mutations don't leak to the input reference.
 */
export const arrayFrom = <T>(value: MaybeReadonlyArray<T>): T[] => (Array.isArray(value) ? [...value] : []);

/**
 * Preserves `undefined` while collapsing `null` to an empty array.
 * Handy when `undefined` conveys "not loaded yet" instead of "empty".
 */
export const optionalArrayFrom = <T>(value: MaybeReadonlyArray<T>): T[] | undefined =>
  value === undefined ? undefined : arrayFrom(value);

/**
 * Alias for callers that conceptually expect an array but still want safety.
 */
export const ensureArray = <T>(value: MaybeReadonlyArray<T>): T[] => arrayFrom(value);

/**
 * Coerces a single value or array-ish into a real array.
 * Returns a shallow copy so mutations don't leak to the input reference.
 */
export const castArray = <T>(value: T | MaybeReadonlyArray<T>): T[] => {
  if (value === null || value === undefined) {
    return [];
  }
  if (isArrayValue(value)) {
    return [...value];
  }
  return [value];
};

export interface EnsureStringArrayOptions {
  readonly filterEmpty?: boolean;
  readonly maxLength?: number;
  readonly trim?: boolean;
}

/**
 * Coerce an unknown value into a string array, trimming entries and applying optional limits.
 */
export const ensureStringArray = (value: unknown, options?: EnsureStringArrayOptions): string[] => {
  const maxLengthCandidate = options?.maxLength;
  const hasMaxLength = typeof maxLengthCandidate === 'number' && Number.isFinite(maxLengthCandidate);
  const limit = hasMaxLength ? Math.max(0, Math.trunc(maxLengthCandidate)) : undefined;
  const trim = typeof options?.trim === 'boolean' ? options.trim : true;
  const filterEmpty = typeof options?.filterEmpty === 'boolean' ? options.filterEmpty : true;

  if (!Array.isArray(value)) {
    return [];
  }

  const results: string[] = [];
  for (const entry of value) {
    if (typeof entry !== 'string') {
      continue;
    }

    const normalized = trim ? entry.trim() : entry;
    if (filterEmpty && normalized.length === 0) {
      continue;
    }

    results.push(normalized);
    if (limit !== undefined && results.length >= limit) {
      break;
    }
  }

  return results;
};

export default arrayFrom;
